import * as Sentry from '@sentry/react';

/**
 * A no-op when VITE_SENTRY_DSN isn't set at build time — Sentry.init() is
 * simply never called, and every Sentry.* call elsewhere (captureException,
 * setUser) stays safe to call regardless, doing nothing until init() has
 * run. Same "optional, silently absent" shape as the backend's Sentry
 * setup and VITE_API_URL.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? 'production' : 'local',
    // Crash reporting runs regardless of cookie consent (see CookieBanner) —
    // it's not the tracking a manager is being asked to opt into, and this
    // keeps it that way: no default PII, no session replay, nothing beyond
    // the stack trace and whatever setUser explicitly attaches (a bare user
    // id — see AuthContext).
    sendDefaultPii: false,
  });
}

/** The one place non-ApiRequestError failures get reported — see api.ts's
 * `request()`, the single function every API call in this app goes
 * through. A no-op alongside initSentry() when Sentry was never
 * initialized, so this is always safe to call. */
export function reportUnexpectedError(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
